import type { AccountInfo, LoginInfo, PtAccount, PtThirdAccount } from '@/api/types/pt_entities'
import type { BindingType, CodeOpType, CodeType } from '@/api/types/pt_enums'
import type { RequestOptions, ResponseModel } from '@/types/pt_types'
import { apiClient } from '@/axios/http_client'

export interface ApiBindingParam {
  accountId?: number // 账号编号
  type?: BindingType // 绑定类型
  value?: string // 绑定值
  options?: RequestOptions
}
export interface ApiChangeSelfPasswordParam {
  oldPwd?: string // 老密码(md5)
  newPwd?: string // 新密码(md5)
  options?: RequestOptions
}
export interface ApiForgetPasswordParam {
  name?: string // 手机/邮箱
  password?: string // 新密码(md5)
  code?: string // 验证码
  options?: RequestOptions
}
export interface ApiGetAccountParam {
  accountId?: number // 账号编号
  options?: RequestOptions
}
export interface ApiGetCodeParam {
  name?: string // 手机号/邮箱
  codeType?: CodeType // 验证码类型
  codeOpType?: CodeOpType // 操作类型
  options?: RequestOptions
}
export interface ApiLoginByCodeParam {
  name?: string // 手机号或邮箱
  code?: string // 验证码
  options?: RequestOptions
}
export interface ApiLoginByPasswordParam {
  name?: string // 手机号/邮箱/账号
  password?: string // 密码(md5)
  options?: RequestOptions
}
export interface ApiLoginByThirdParam {
  type?: BindingType // 绑定类型
  uuid?: string // 唯一标识
  phone?: string // 手机号
  checkCode?: string // 登录验证码
  phoneCode?: string // 手机号验证码
  options?: RequestOptions
}
export interface ApiLogoutParam {
  options?: RequestOptions
}
export interface ApiRefreshTokenParam {
  refreshToken?: string // 刷新令牌
  options?: RequestOptions
}
export interface ApiSaveThirdAccountParam {
  accounts?: Array<PtThirdAccount> // 账号信息
  options?: RequestOptions
}
export interface ApiSelfBindingParam {
  type?: BindingType // 绑定类型
  value?: string // 绑定值
  code?: string // 验证码
  force?: boolean // 强制绑定
  options?: RequestOptions
}
export interface ApiSelfUnbindingParam {
  type?: BindingType // 绑定类型
  code?: string // 验证码
  options?: RequestOptions
}
export interface ApiUnbindingParam {
  accountId?: number // 账号编号
  type?: BindingType // 绑定类型
  options?: RequestOptions
}
export interface ApiUpdateAccountParam {
  accountId?: number // 账号编号
  request?: PtAccount // 账号信息
  options?: RequestOptions
}
export interface ApiUpdatePasswordParam {
  accountId?: number // 账号编号
  password?: string // 新密码(md5)
  options?: RequestOptions
}

/**
 * 
 * Created by hongsq.
 */

/**
  * 设置绑定 - 设置绑定
  * @param accountId 账号编号
  * @param type 绑定类型
  * @param value 绑定值
  */
export const apiBinding = async (params: ApiBindingParam = {}): Promise<void> => {
  const {  accountId, type, value, options  } = params
  const resp = await apiClient.formPost<ResponseModel<void>>({
    data: { accountId, type, value },
    checkToken: true,
    url: '/api/account/binding',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 修改自己的密码 - 修改自己的密码
  * @param oldPwd 老密码(md5)
  * @param newPwd 新密码(md5)
  */
export const apiChangeSelfPassword = async (params: ApiChangeSelfPasswordParam = {}): Promise<void> => {
  const {  oldPwd, newPwd, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { oldPwd, newPwd },
    checkToken: true,
    url: '/api/account/password',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 忘记密码 - 通过忘记密码修改密码
  * @param name 手机/邮箱
  * @param password 新密码(md5)
  * @param code 验证码
  */
export const apiForgetPassword = async (params: ApiForgetPasswordParam = {}): Promise<void> => {
  const {  name, password, code, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { name, password, code },
    checkToken: false,
    url: '/api/account/password/forget',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 查询账号信息 - 查询账号信息
  * @param accountId 账号编号
  */
export const apiGetAccount = async (params: ApiGetAccountParam = {}): Promise<AccountInfo> => {
  const {  accountId, options  } = params
  const resp = await apiClient.get<ResponseModel<AccountInfo>>({
    params: {},
    checkToken: true,
    url: `/api/account/${accountId}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 获取验证码 - 绑定的时候直接指定手机号或邮箱,解绑的时候指定的是验证码类型
  * @param name 手机号/邮箱
  * @param codeType 验证码类型
  * @param codeOpType 操作类型
  */
export const apiGetCode = async (params: ApiGetCodeParam = {}): Promise<void> => {
  const {  name, codeType, codeOpType, options  } = params
  const resp = await apiClient.get<ResponseModel<void>>({
    params: { name, codeType, codeOpType },
    checkToken: 'MAYBE',
    url: '/api/account/code',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 验证码登录 - 使用手机和验证码登录
  * @param name 手机号或邮箱
  * @param code 验证码
  */
export const apiLoginByCode = async (params: ApiLoginByCodeParam = {}): Promise<LoginInfo> => {
  const {  name, code, options  } = params
  const resp = await apiClient.formPut<ResponseModel<LoginInfo>>({
    data: { name, code },
    checkToken: false,
    url: '/api/account/login/code',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 用户名密码登录 - 使用用户名密码登录
  * @param name 手机号/邮箱/账号
  * @param password 密码(md5)
  */
export const apiLoginByPassword = async (params: ApiLoginByPasswordParam = {}): Promise<LoginInfo> => {
  const {  name, password, options  } = params
  const resp = await apiClient.formPut<ResponseModel<LoginInfo>>({
    data: { name, password },
    checkToken: false,
    url: '/api/account/login/pwd',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 使用第三方账号登录 - 使用第三方账号登录
  * @param type 绑定类型
  * @param uuid 唯一标识
  * @param phone 手机号
  * @param checkCode 登录验证码
  * @param phoneCode 手机号验证码
  */
export const apiLoginByThird = async (params: ApiLoginByThirdParam = {}): Promise<LoginInfo> => {
  const {  type, uuid, phone, checkCode, phoneCode, options  } = params
  const resp = await apiClient.formPut<ResponseModel<LoginInfo>>({
    data: { type, uuid, phone, checkCode, phoneCode },
    checkToken: false,
    url: `/api/account/login/${type}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 退出登录 - 退出登录, 清空登录信息
  */
export const apiLogout = async (params: ApiLogoutParam = {}): Promise<void> => {
  const {  options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: {},
    checkToken: 'MAYBE',
    url: '/api/account/logout',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 刷新AccessToken - 使用RefreshToken刷新AccessToken
  * @param refreshToken 刷新令牌
  */
export const apiRefreshToken = async (params: ApiRefreshTokenParam = {}): Promise<LoginInfo> => {
  const {  refreshToken, options  } = params
  const resp = await apiClient.formPost<ResponseModel<LoginInfo>>({
    data: { refreshToken },
    checkToken: 'MAYBE',
    url: '/api/account/token/refresh',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 保存第三方平台账号信息 - 保存第三方平台账号信息
  * @param accounts 账号信息
  */
export const apiSaveThirdAccount = async (params: ApiSaveThirdAccountParam = {}): Promise<void> => {
  const {  accounts, options  } = params
  const resp = await apiClient.jsonPost<ResponseModel<void>>({
    data: { ...(accounts || {}) },
    checkToken: true,
    url: '/api/account/third',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 绑定 - 设置自己的绑定
  * @param type 绑定类型
  * @param value 绑定值
  * @param code 验证码
  * @param force 强制绑定
  */
export const apiSelfBinding = async (params: ApiSelfBindingParam = {}): Promise<void> => {
  const {  type, value, code, force, options  } = params
  const resp = await apiClient.formPost<ResponseModel<void>>({
    data: { type, value, code, force },
    checkToken: true,
    url: '/api/account/self/binding',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 解绑 - 解绑自己
  * @param type 绑定类型
  * @param code 验证码
  */
export const apiSelfUnbinding = async (params: ApiSelfUnbindingParam = {}): Promise<void> => {
  const {  type, code, options  } = params
  const resp = await apiClient.formPost<ResponseModel<void>>({
    data: { type, code },
    checkToken: true,
    url: '/api/account/self/unbinding',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 解绑 - 解绑
  * @param accountId 账号编号
  * @param type 绑定类型
  */
export const apiUnbinding = async (params: ApiUnbindingParam = {}): Promise<void> => {
  const {  accountId, type, options  } = params
  const resp = await apiClient.formPost<ResponseModel<void>>({
    data: { accountId, type },
    checkToken: true,
    url: '/api/account/unbinding',
    apiOptions: options
  })
  return resp.data!
}

/**
  * 修改用户 - 修改用户信息
  * @param accountId 账号编号
  * @param request 账号信息
  */
export const apiUpdateAccount = async (params: ApiUpdateAccountParam = {}): Promise<void> => {
  const {  accountId, request, options  } = params
  const resp = await apiClient.jsonPut<ResponseModel<void>>({
    data: { ...(request || {}) },
    checkToken: true,
    url: `/api/account/${accountId}`,
    apiOptions: options
  })
  return resp.data!
}

/**
  * 设置密码 - 为设置密码
  * @param accountId 账号编号
  * @param password 新密码(md5)
  */
export const apiUpdatePassword = async (params: ApiUpdatePasswordParam = {}): Promise<void> => {
  const {  accountId, password, options  } = params
  const resp = await apiClient.formPut<ResponseModel<void>>({
    data: { accountId, password },
    checkToken: true,
    url: '/api/account/set/password',
    apiOptions: options
  })
  return resp.data!
}
